// src/pages/FollowUpVisitNote.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc, getDoc, setDoc, addDoc, collection,
  serverTimestamp, query, where, orderBy, getDocs
} from "firebase/firestore";
import { db } from "../firebase";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { Save, ArrowLeft, CheckCircle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import SmartCaptureModal from "../components/SmartCaptureModal";
import { apiPost } from "../utils/api";

const NoteSectionTextarea = ({ label, ...props }) => (
  <div>
    <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">{label}</label>
    <textarea
      {...props}
      className="w-full h-40 bg-slate-100 dark:bg-zinc-800 rounded-lg p-4 text-gray-900 dark:text-white border-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

export default function FollowUpVisitNote() {
  const { patientId, noteId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showSmartCapture, setShowSmartCapture] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [activeTab, setActiveTab] = useState("Visit");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [noteData, setNoteData] = useState({
    intervalHistory: "",
    reviewOfSystems: "",
    priorProblemProgress: "",
    newSymptoms: "",
    newConcerns: "",
    medicationChanges: "",
    sideEffects: "",
    medicationAdherence: "",
    updatedPlan: "",
    type: "Clinic Follow-Up",
  });

  const noteTabs = ["Visit", "Progress", "New Issues", "Meds", "Plan"];

  // ───────────────────────────────────────────
  // FETCH PATIENT NAME
  // ───────────────────────────────────────────
  const fetchPatientName = useCallback(async () => {
    const snap = await getDoc(doc(db, "patients", patientId));
    if (snap.exists()) {
      const d = snap.data();
      setPatientName(`${d.firstName || ""} ${d.lastName || ""}`.trim());
    }
  }, [patientId]);

  useEffect(() => {
    fetchPatientName();

    if (noteId && noteId !== "new") {
      getDoc(doc(db, "notes", noteId)).then(snap => {
        if (snap.exists()) setNoteData(prev => ({ ...prev, ...snap.data() }));
      });
    }
  }, [noteId, patientId, fetchPatientName]);

  const handleChange = e =>
    setNoteData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // ───────────────────────────────────────────
  // SAVE NOTE
  // ───────────────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);

    const summary = `Interval History: ${noteData.intervalHistory}\nUpdated Plan: ${noteData.updatedPlan}`;
    const payload = { ...noteData, summary, timestamp: serverTimestamp() };

    if (noteId && noteId !== "new") {
      await setDoc(doc(db, "notes", noteId), payload, { merge: true });
    } else {
      const ref = await addDoc(collection(db, "notes"), {
        ...payload,
        ownerId: currentUser.uid,
        patientID: patientId,
      });
      navigate(`/follow-up-visit-note/${patientId}/${ref.id}`, { replace: true });
    }

    setIsSaving(false);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  // ───────────────────────────────────────────
  // GENERATE UPDATED PLAN → /v1/generate-ap-v2
  // ───────────────────────────────────────────
  const handleGeneratePlan = async () => {
    setIsGenerating(true);

    try {
      const q = query(
        collection(db, "notes"),
        where("patientID", "==", patientId),
        orderBy("timestamp", "desc")
      );

      const snap = await getDocs(q);

      const past_notes_context = snap.docs
        .map(d => {
          const n = d.data();
          const ts = n.timestamp?.toDate?.().toLocaleDateString() || "N/A";
          return `--- Past Note (${ts}) ---\n${n.summary || ""}`;
        })
        .join("\n\n");

      const current_note_context = `Interval History: ${noteData.intervalHistory}\nReview: ${noteData.reviewOfSystems}`;

      const payload = {
        context: "outpatient",
        current_note_context,
        past_notes_context,
      };

      const result = await apiPost("/generate-ap-v2", payload);
      setNoteData(prev => ({ ...prev, updatedPlan: result.summary || prev.updatedPlan }));
    } catch (err) {
      setNoteData(prev => ({ ...prev, updatedPlan: `Error: ${err.message}` }));
    }

    setIsGenerating(false);
  };

  // ───────────────────────────────────────────
  // SMART CAPTURE INPUT → /v1/format-followup-text
  // ───────────────────────────────────────────
  const handleSmartCaptureSubmit = async (raw) => {
    let extractedText = "";

    if (raw.type === "audio") {
      const form = new FormData();
      form.append("file", raw.audioBlob, "audio.mp3");
      const res = await apiPost("/transcribe-followup", form, true);
      extractedText = res.text;
    }

    if (raw.type === "image") {
      const res = await apiPost("/extract-text-from-image", {
        image: raw.imageBase64,
      });
      extractedText = res.text;
    }

    if (raw.type === "text") extractedText = raw.text;

    const out = await apiPost("/format-followup-text", { text: extractedText });

    setNoteData(prev => ({
      ...prev,
      intervalHistory: out.interval_history || prev.intervalHistory,
      reviewOfSystems: out.review_of_systems || prev.reviewOfSystems,
      priorProblemProgress: out.prior_problem_progress || prev.priorProblemProgress,
      newSymptoms: out.new_symptoms || prev.newSymptoms,
      newConcerns: out.new_concerns || prev.newConcerns,
      medicationChanges: out.medication_changes || prev.medicationChanges,
      sideEffects: out.side_effects || prev.sideEffects,
      medicationAdherence: out.medication_adherence || prev.medicationAdherence,
      updatedPlan: out.updated_plan || prev.updatedPlan,
    }));
  };

  // ───────────────────────────────────────────
  // RENDER UI
  // ───────────────────────────────────────────
  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />

      <main className="max-w-5xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl">

          {/* HEADER */}
          <div className="p-6 border-b border-gray-200 dark:border-zinc-800 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Follow-Up Visit Note</h1>
                <p className="text-gray-500 dark:text-gray-400">for {patientName}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowSmartCapture(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold"
              >
                <Sparkles size={16} /> Smart Capture
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving || isSaved}
                className={`flex items-center gap-2 px-8 py-2 text-white rounded-full font-semibold transition ${
                  isSaved ? "bg-green-500" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent animate-spin rounded-full"/>
                ) : isSaved ? (
                  <>
                    <CheckCircle size={18}/> Saved!
                  </>
                ) : (
                  <>
                    <Save size={18}/> Save Note
                  </>
                )}
              </button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-6">

            {/* TABS */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1 space-x-2">
                {noteTabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="relative px-5 py-1.5 text-sm font-semibold"
                  >
                    <span className={`relative z-10 ${
                      activeTab === tab ? "text-gray-900 dark:text-white" : "text-gray-500"
                    }`}>{tab}</span>
                    {activeTab === tab && (
                      <motion.div
                        layoutId="pill"
                        className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* TAB CONTENT */}
            {activeTab === "Visit" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  name="intervalHistory"
                  label="Interval History"
                  value={noteData.intervalHistory}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  name="reviewOfSystems"
                  label="Review of Systems"
                  value={noteData.reviewOfSystems}
                  onChange={handleChange}
                />
              </div>
            )}

            {activeTab === "Progress" && (
              <NoteSectionTextarea
                name="priorProblemProgress"
                label="Status of Prior Problems"
                value={noteData.priorProblemProgress}
                onChange={handleChange}
              />
            )}

            {activeTab === "New Issues" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  name="newSymptoms"
                  label="New Symptoms"
                  value={noteData.newSymptoms}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  name="newConcerns"
                  label="New Concerns / Diagnoses"
                  value={noteData.newConcerns}
                  onChange={handleChange}
                />
              </div>
            )}

            {activeTab === "Meds" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  name="medicationChanges"
                  label="Medication Changes"
                  value={noteData.medicationChanges}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  name="sideEffects"
                  label="Side Effects"
                  value={noteData.sideEffects}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  name="medicationAdherence"
                  label="Medication Adherence"
                  value={noteData.medicationAdherence}
                  onChange={handleChange}
                />
              </div>
            )}

            {activeTab === "Plan" && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  name="updatedPlan"
                  label="Updated Plan"
                  value={noteData.updatedPlan}
                  onChange={handleChange}
                />

                <button
                  onClick={handleGeneratePlan}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full"
                >
                  {isGenerating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent animate-spin rounded-full"/>
                  ) : (
                    <>
                      <Sparkles size={16}/> Generate Plan
                    </>
                  )}
                </button>
              </div>
            )}

          </div>
        </div>
      </main>

      <SmartCaptureModal
        show={showSmartCapture}
        onClose={() => setShowSmartCapture(false)}
        onTextExtracted={handleSmartCaptureSubmit}
      />
    </div>
  );
}
